import {
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  message,
  Modal,
  Row,
  Spin,
  Table,
} from "antd";
import dayjs from "dayjs";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import SelectStatusPayment from "../../../components/SelectStatusPayment";
import API from "../../../services/api-request";
import { capitalizeLetter } from "../../../utils/capitalizeLetter";
import { getDataLogin } from "../../../utils/getDataLogin";

const DetailInvoice = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [editForm] = Form.useForm();
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingBtn, setLoadingBtn] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [openCheck, setOpenCheck] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const [isNeedCheck, setIsNeedCheck] = useState(false);

  const isAccounting =
    getDataLogin()?.privilege?.name?.toLowerCase() === "accounting";

  const isAccountingOrManagement =
    getDataLogin()?.privilege?.name?.toLowerCase() === "accounting" ||
    getDataLogin()?.privilege?.name?.toLowerCase() === "management";

  const getDataDetail = useCallback((id) => {
    setLoading(true);
    API.Get(`invoice/detail/${id}`)
      .then((result) => {
        setLoading(false);

        if (result?.data?.payment_verification === "checked") {
          setIsChecked(true);
        }

        if (
          result?.data?.payment_verification === "pending" ||
          result?.data?.nota_verification === "pending"
        ) {
          setIsNeedCheck(true);
        }

        const transformedItem = {
          ...result?.data,
          status:
            result?.data?.status === "checked"
              ? capitalizeLetter(result?.data?.status)
              : result?.data?.status,
          paid_at: result?.data?.paid_at ? dayjs(result?.data?.paid_at) : "",
          amount: result?.data?.amount?.toLocaleString("id-ID"),
          client: result?.data?.client?.name,
          grand_total: result?.data?.grand_total?.toLocaleString("id-ID"),
          total_material_handling_fee_cost: result?.data
            ?.total_material_handling_fee_cost
            ? result?.data?.total_material_handling_fee_cost.toLocaleString(
                "id-ID",
              )
            : 0,
          nota_verification: capitalizeLetter(result?.data?.nota_verification),
          payment_verification: capitalizeLetter(
            result?.data?.payment_verification,
          ),
          created_at: result?.data?.created_at
            ? dayjs(result?.data?.created_at).format("YYYY-MM-DD")
            : "",
        };

        editForm.setFieldsValue(transformedItem);

        setData(transformedItem);
      })
      .catch((error) => {
        console.log(error);
        setLoading(false);
        message.info("Failed Fetch Request Invoice Detail");
      });
  }, [editForm]);

  useEffect(() => {
    if (id) {
      getDataDetail(id);
    }
  }, [id, getDataDetail]);

  const onFinish = async (values, isChecking = false) => {
    setLoadingBtn(true);
    try {
      const { status, paid_at } = values;

      let dataForm = {
        status: status === "Lunas" ? "paid" : "pending",
        paid_at: dayjs(paid_at).format("YYYY-MM-DD"),
      };

      if (isAccounting && isChecking) {
        setConfirmLoading(true);

        if (data?.nota_verification === "Pending") {
          await API.Post(`invoice/checked/`, { id: id });
        } else {
          await API.Post(`invoice/checked-payment/`, { id: id });
        }

        message.success("Invoice has been checked");
      } else {
        await API.Patch(`invoice/update/${id}`, dataForm);
        message.success("Invoice has been updated");
      }

      editForm.resetFields();
      handleBack();
    } catch (err) {
      console.log(err);
      message.info(`${err?.response?.data?.error}`);
    } finally {
      setConfirmLoading(false);
      setLoadingBtn(false);
    }
  };

  const onFinishFailed = (errorInfo) => {
    console.log("Failed:", errorInfo);
  };

  const handleBack = () => {
    navigate(-1);
  };

  const columnsItem = [
    {
      title: "No",
      dataIndex: "no",
      key: "no",
      width: 60,
      render: (text, record, index) => index + 1,
    },
    {
      title: "Jenis Produk",
      dataIndex: "product",
      key: "product",
      sorter: (a, b) => a.product?.name - b.product?.name,
      render: (product) => {
        if (product?.name === null || product?.name === "") {
          return "-";
        } else {
          return product?.name;
        }
      },
    },
    {
      title: "Berat Keluar",
      dataIndex: "weight",
      key: "weight",
      sorter: (a, b) => a.weight - b.weight,
      render: (weight) => {
        if (weight === null || weight === "") {
          return "-";
        } else {
          return `${weight.toLocaleString("id-ID", {
            minimumFractionDigits: 1,
            maximumFractionDigits: 1,
          })} Kg`;
        }
      },
    },
    {
      title: "Jumlah Pack",
      dataIndex: "qty",
      key: "qty",
      sorter: (a, b) => a.qty - b.qty,
      render: (qty) => {
        if (qty === null || qty === "") {
          return "-";
        } else {
          return qty;
        }
      },
    },
    {
      title: "Spesifikasi",
      dataIndex: "specification",
      key: "specification",
      sorter: (a, b) => a.specification - b.specification,
      render: (specification) => {
        if (specification === null || specification === "") {
          return "-";
        } else {
          return specification;
        }
      },
    },
    {
      title: "Catatan Sistem",
      dataIndex: "note",
      key: "note",
      render: (note) => {
        if (!note) return <span>-</span>;

        const regex = /\(\s*[+-]?\d+([.,]\d+)?\s*kg\s*\)/gi;
        const elements = [];

        let lastIndex = 0;

        for (const match of note.matchAll(regex)) {
          const start = match.index;
          const end = start + match[0].length;

          // text before the match
          if (start > lastIndex) {
            elements.push(
              <span key={lastIndex}>{note.slice(lastIndex, start)}</span>,
            );
          }

          const isNegative = /\(\s*-/.test(match[0]);

          // the matched (xxx kg) part
          elements.push(
            <span
              key={start}
              style={{
                color: isNegative ? "red" : "green",
                fontWeight: 500,
              }}
            >
              {match[0]}
            </span>,
          );

          lastIndex = end;
        }

        // remaining text after last match
        if (lastIndex < note.length) {
          elements.push(<span key={lastIndex}>{note.slice(lastIndex)}</span>);
        }

        return <span>{elements}</span>;
      },
    },
    {
      title: "Total",
      dataIndex: "amount",
      key: "amount",
      sorter: (a, b) => a.amount - b.amount,
      render: (amount) => {
        if (amount === null || amount === "") {
          return "-";
        } else {
          return `Rp. ${amount.toLocaleString("id-ID")}`;
        }
      },
    },
    {
      title: "Tanggal Inbound",
      dataIndex: "inbound_date",
      key: "inbound_date",
      sorter: (a, b) => a.inbound_date - b.inbound_date,
      render: (inbound_date) => {
        if (inbound_date === null || inbound_date === "") {
          return "-";
        } else {
          return dayjs(inbound_date).format("YYYY-MM-DD");
        }
      },
    },
    {
      title: "Tanggal Outbound",
      dataIndex: "outbound_date",
      key: "outbound_date",
      sorter: (a, b) => a.outbound_date - b.outbound_date,
      render: (outbound_date) => {
        if (outbound_date === null || outbound_date === "") {
          return "-";
        } else {
          return dayjs(outbound_date).format("YYYY-MM-DD");
        }
      },
    },
  ];

  return (
    <>
      {loading ? (
        <div
          style={{
            width: "100%",
            height: "100vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Spin />
        </div>
      ) : (
        <Form
          form={editForm}
          name="Edit Form"
          style={{
            margin: "0 auto",
          }}
          layout="vertical"
          autoComplete="off"
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
        >
          <Row gutter={[8, 8]}>
            <Col lg={12} md={12} sm={12} xs={24}>
              <Form.Item
                label="Nomor"
                name="invoice_number"
                rules={[
                  {
                    required: true,
                    message: "Please input your Nomor!",
                  },
                ]}
              >
                <Input placeholder="Nomor" readOnly />
              </Form.Item>
            </Col>
            <Col lg={12} md={12} sm={12} xs={24}>
              <Form.Item
                label="Customer Name"
                name="client"
                rules={[
                  {
                    required: true,
                    message: "Please input your Customer Name!",
                  },
                ]}
              >
                <Input placeholder="Customer Name" readOnly />
              </Form.Item>
            </Col>
            <Col lg={12} md={12} sm={12} xs={24}>
              <Form.Item label="Tanggal Terbit Invoice" name="created_at">
                <Input placeholder="Tanggal Terbit Invoice" readOnly />
              </Form.Item>
            </Col>
            <Col lg={12} md={12} sm={12} xs={24}>
              <Form.Item
                label="Tanggal Bayar"
                name="paid_at"
                rules={[
                  {
                    required: true,
                    message: "Please input your Tanggal Bayar!",
                  },
                ]}
              >
                <DatePicker
                  style={{ width: "100%" }}
                  placeholder="Pilih Tanggal Bayar"
                  inputReadOnly={isChecked || !isAccountingOrManagement}
                  open={
                    isChecked || !isAccountingOrManagement ? false : undefined
                  }
                  allowClear={false}
                  onChange={() => editForm.setFieldValue("status", "Lunas")}
                  minDate={dayjs(editForm.getFieldValue("created_at"))}
                />
              </Form.Item>
            </Col>
            <Col lg={12} md={12} sm={12} xs={24}>
              <Form.Item
                label="Total"
                name="grand_total"
                rules={[
                  {
                    required: true,
                    message: "Please input your Total!",
                  },
                ]}
              >
                <Input prefix="Rp" placeholder="Total" readOnly />
              </Form.Item>
            </Col>
            {isChecked || !isAccountingOrManagement ? (
              <Col lg={12} md={12} sm={12} xs={24}>
                <Form.Item
                  label="Status"
                  name="status"
                  rules={[
                    {
                      required: true,
                      message: "Please input your Status!",
                    },
                  ]}
                >
                  <SelectStatusPayment readOnly />
                </Form.Item>
              </Col>
            ) : (
              <Col lg={12} md={12} sm={12} xs={24}>
                <Form.Item
                  label="Status"
                  name="status"
                  rules={[
                    {
                      required: true,
                      message: "Please input your Status!",
                    },
                  ]}
                >
                  <SelectStatusPayment />
                </Form.Item>
              </Col>
            )}
            <Col lg={12} md={12} sm={12} xs={24}>
              <Form.Item
                label="Status Verifikasi Nota"
                name="nota_verification"
              >
                <Input placeholder="Status Verifikasi Nota" readOnly />
              </Form.Item>
            </Col>
            <Col lg={12} md={12} sm={12} xs={24}>
              <Form.Item
                label="Status Verifikasi Pembayaran"
                name="payment_verification"
              >
                <Input placeholder="Status Verifikasi Pembayaran" readOnly />
              </Form.Item>
            </Col>
            <Col lg={12} md={12} sm={12} xs={24}>
              <Form.Item
                label="Total Material Handling Fee"
                name="total_material_handling_fee_cost"
              >
                <Input
                  prefix="Rp"
                  placeholder="Total Material Handling Fee"
                  readOnly
                />
              </Form.Item>
            </Col>
            <Col lg={24} md={24} sm={24} xs={24}></Col>
            <Col lg={24} md={24} sm={24} xs={24}>
              <div style={{ fontWeight: 600, marginBottom: 10 }}>
                Item Invoice
              </div>
            </Col>
            <Col lg={24} md={24} sm={24} xs={24}>
              <Table
                dataSource={data.invoice_items}
                columns={columnsItem}
                pagination={false}
                rowKey={(dataSource) => dataSource.id}
              />
            </Col>
            <Col
              lg={isNeedCheck ? 12 : 24}
              md={isNeedCheck ? 12 : 24}
              sm={isNeedCheck ? 12 : 24}
              xs={24}
            >
              <Button
                style={{
                  width: "100%",
                  backgroundColor: "orange",
                  borderColor: "orange",
                  color: "white",
                }}
                type="primary"
                onClick={() => handleBack()}
              >
                Kembali
              </Button>
            </Col>
            {!isChecked && isAccountingOrManagement && (
              <Col lg={12} md={12} sm={12} xs={24}>
                <Button
                  key="submit"
                  htmlType="submit"
                  style={buttonStyle}
                  type="primary"
                  loading={loadingBtn}
                >
                  Simpan
                </Button>
              </Col>
            )}
            {isAccounting && isNeedCheck && (
              <Col lg={24} md={24} sm={24} xs={24}>
                <Button
                  onClick={() => setOpenCheck(true)}
                  style={{ ...buttonStyle, backgroundColor: "#4cb050" }}
                  type="primary"
                  loading={loadingBtn}
                >
                  {data?.nota_verification === "Pending"
                    ? "Verifikasi Nota"
                    : "Verifikasi Pembayaran"}
                </Button>
              </Col>
            )}
          </Row>
        </Form>
      )}

      <Modal
        title="Konfirmasi"
        okText={"Simpan"}
        cancelText="Batal"
        open={openCheck}
        onOk={(values) => onFinish(values, true)}
        confirmLoading={confirmLoading}
        onCancel={() => setOpenCheck(false)}
        okType={"primary"}
      >
        <p>
          Anda akan melakukan verifikasi {!isChecked ? "nota" : "pembayaran"}.
          Apakah ingin menyimpan data?
        </p>
      </Modal>
    </>
  );
};

const buttonStyle = {
  width: "100%",
};


DetailInvoice.propTypes = {

};

export default DetailInvoice;
